import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, type FirestoreError } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { PaintingSchema } from '../lib/schemas/painting';
import type { Painting } from '../types/painting';

type UsePaintingsResult =
  | { paintings: Painting[]; loading: true; error: null }
  | { paintings: Painting[]; loading: false; error: null }
  | { paintings: Painting[]; loading: false; error: Error };

export function usePaintings(): UsePaintingsResult {
  const [paintings, setPaintings] = useState<Painting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const paintingsQuery = query(collection(db, 'paintings'), orderBy('createdAt'));

    const unsubscribe = onSnapshot(
      paintingsQuery,
      (snapshot): void => {
        try {
          const parsed = snapshot.docs.map((doc) => PaintingSchema.parse({ id: doc.id, ...doc.data() }));
          setPaintings(parsed);
          setError(null);
        } catch (parseError: unknown) {
          setError(parseError instanceof Error ? parseError : new Error('Failed to parse painting data'));
        } finally {
          setLoading(false);
        }
      },
      (snapshotError: FirestoreError): void => {
        setError(snapshotError);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, []);

  if (loading) return { paintings: [], loading: true, error: null };
  if (error !== null) return { paintings: [], loading: false, error };
  return { paintings, loading: false, error: null };
}
